<script lang="ts">
  import { onMount } from 'svelte';
  import {
    PIECE_IDS,
    type ArenaMap,
    type PieceId,
    type PlacedPiece,
  } from '@arena/shared';
  import {
    EditorEngine,
    MAX_DUMMIES,
    type TerrainBrush,
    type Tool,
  } from '../editor/EditorEngine';
  import { send } from '../lib/net.svelte';

  let canvas: HTMLCanvasElement;
  let engine: EditorEngine | undefined = $state();
  let maps = $state<string[]>([]);
  let name = $state('Untitled');
  let tool = $state<Tool>({ kind: 'piece', piece: 'floor' });
  let level = $state(0);
  let rot = $state(0);
  let scale = $state(1);
  let selected = $state<PlacedPiece | null>(null);
  let brush = $state<TerrainBrush>('raise');
  let radius = $state(1);
  let strength = $state(0.5);
  let filter = $state('');
  /** Saving writes `assets/hub.json` instead of an arena in `maps/`. */
  let editingHub = $state(false);
  let counts = $state({ pieces: 0, spawns: 0, dummies: 0 });
  let status = $state('');

  const sync = () => {
    if (!engine) return;
    tool = engine.tool;
    level = engine.level;
    rot = engine.rot;
    scale = engine.scale;
    brush = engine.brush;
    radius = engine.radius;
    strength = engine.strength;
    selected =
      engine.selected === null
        ? null
        : { ...engine.map.pieces[engine.selected] };
    name = engine.map.name;
    counts = {
      pieces: engine.map.pieces.length,
      spawns: engine.map.spawns.length,
      dummies: engine.map.dummies?.length ?? 0,
    };
  };

  onMount(() => {
    const editor = new EditorEngine(canvas, sync);
    engine = editor;
    void editor.start();
    void refreshMaps().then(() => {
      if (maps.includes('ruins')) void open('ruins');
    });
    return () => editor.dispose();
  });

  async function refreshMaps() {
    maps = await fetch('/api/maps').then((r) => r.json());
  }

  async function open(file: string) {
    const map: ArenaMap = await fetch(
      file === 'hub' ? '/hub.json' : `/api/maps/${file}`,
    ).then((r) => r.json());
    engine?.load(map);
    editingHub = file === 'hub';
    status = `Loaded ${map.name}`;
  }

  async function save(): Promise<string | null> {
    if (!engine) return null;
    engine.map.name = name.trim() || 'Untitled';
    const res = await fetch(editingHub ? '/api/hub' : '/api/maps', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(engine.map),
    });
    const body = await res.json();
    if (!res.ok) {
      status = `Save failed: ${body.error}`;
      return null;
    }
    // The server swaps the hub in at once; open game tabs still draw the one they loaded.
    status = editingHub
      ? 'Saved hub.json; reload any open game tab to see it'
      : `Saved as ${body.file}.json`;
    await refreshMaps();
    return body.file;
  }

  async function practice() {
    const file = await save();
    if (!file) return;
    send({ t: 'queue', bracket: 'practice', map: file });
    location.hash = '#/';
  }

  function download() {
    if (!engine) return;
    const blob = new Blob([JSON.stringify({ ...engine.map, name }, null, 2)], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${name || 'map'}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const isPiece = (id: string) => tool.kind === 'piece' && tool.piece === id;

  const BRUSHES: [TerrainBrush, string][] = [
    ['raise', 'Raise'],
    ['lower', 'Lower'],
    ['flatten', 'Flatten'],
    ['set', 'Set to level'],
    ['smooth', 'Smooth'],
    ['erase', 'Erase'],
  ];

  // First match wins, so bridges with pillars land in Platforms, not Towers.
  const GROUPS: [string, RegExp][] = [
    ['Floors', /floor|ground|patch/],
    ['Walls & gates', /wall|gate|fence|border/],
    ['Stairs, platforms & bridges', /stairs|block|bridge|structure/],
    ['Towers & columns', /tower|column|pillar/],
    ['Nature', /tree|pine|palm|rocks/],
    ['Banners & lights', /banner|flag|candle|lightpost|lantern|fire-basket/],
    ['Ships & siege', /ship|boat|siege|cannon/],
  ];
  const groupOf = (id: PieceId) =>
    GROUPS.find(([, pattern]) => pattern.test(id))?.[0] ?? 'Props';
  const groups = [...GROUPS.map(([group]) => group), 'Props'].map(
    (group) =>
      [group, PIECE_IDS.filter((id) => groupOf(id) === group)] as const,
  );
  const shown = $derived(
    groups
      .map(
        ([group, ids]) =>
          [group, ids.filter((id) => id.includes(filter.trim()))] as const,
      )
      .filter(([, ids]) => ids.length > 0),
  );

  /** Applies a number typed into the inspector, ignoring blanks and junk. */
  const field =
    (patch: (value: number) => Partial<PlacedPiece>) =>
    (e: Event & { currentTarget: HTMLInputElement }) => {
      const value = e.currentTarget.valueAsNumber;
      if (Number.isFinite(value)) engine?.updateSelected(patch(value));
    };
</script>

<div class="editor">
  <aside class="panel">
    <a href="#/">← Lobby</a>
    <h2>Map editor</h2>
    <label>Name <input bind:value={name} maxlength="40" /></label>
    <div class="row">
      <button onclick={save}>{editingHub ? 'Save hub' : 'Save'}</button>
      {#if !editingHub}
        <button onclick={practice}>Practice here</button>
      {/if}
      <button onclick={download}>Download</button>
      <button
        onclick={() => {
          editingHub = false;
          engine?.load({ name: 'Untitled', pieces: [], spawns: [] });
        }}>New</button
      >
    </div>
    <div class="row">
      <button
        class="small"
        class:active={editingHub}
        onclick={() => open('hub')}>hub</button
      >
      {#each maps as file (file)}
        <button class="small" onclick={() => open(file)}>{file}</button>
      {/each}
    </div>

    <h3>Pieces</h3>
    <input class="search" placeholder="Filter pieces" bind:value={filter} />
    {#each shown as [group, ids] (group)}
      <details open={filter.trim() !== '' || ids.some(isPiece)}>
        <summary>{group} <span class="muted">({ids.length})</span></summary>
        <div class="palette">
          {#each ids as id (id)}
            <button
              class:active={isPiece(id)}
              onclick={() => engine?.setTool({ kind: 'piece', piece: id })}
              >{id}</button
            >
          {/each}
        </div>
      </details>
    {/each}
    <h3>Tools</h3>
    <div class="row">
      <button
        class:active={tool.kind === 'select'}
        onclick={() => engine?.setTool({ kind: 'select' })}>Select</button
      >
      <button
        class:active={tool.kind === 'spawn' && tool.team === 0}
        onclick={() => engine?.setTool({ kind: 'spawn', team: 0 })}
        >Spawn: blue</button
      >
      <button
        class:active={tool.kind === 'spawn' && tool.team === 1}
        onclick={() => engine?.setTool({ kind: 'spawn', team: 1 })}
        >Spawn: red</button
      >
      <button
        class:active={tool.kind === 'dummy'}
        title="Where practice stands its training dummies"
        onclick={() => engine?.setTool({ kind: 'dummy' })}>Dummy</button
      >
      <button
        class:active={tool.kind === 'terrain'}
        onclick={() => engine?.setTool({ kind: 'terrain' })}>Terrain</button
      >
      <button
        class:active={tool.kind === 'erase'}
        onclick={() => engine?.setTool({ kind: 'erase' })}>Erase</button
      >
    </div>
    <div class="row">
      <span>Level {level}</span>
      <button onclick={() => engine?.setLevel(level - 1)}>−</button>
      <button onclick={() => engine?.setLevel(level + 1)}>+</button>
      <span>Rotation {rot * 90}°</span>
      <button onclick={() => engine?.rotate()}>Rotate</button>
      <button onclick={() => engine?.undo()}>Undo</button>
    </div>
    <div class="row">
      <span>Scale {scale}</span>
      <button onclick={() => engine?.setScale(scale - 0.25)}>−</button>
      <button onclick={() => engine?.setScale(scale + 0.25)}>+</button>
    </div>
    {#if tool.kind === 'terrain'}
      <h3>Terrain brush</h3>
      <div class="row">
        {#each BRUSHES as [id, label] (id)}
          <button
            class="small"
            class:active={brush === id}
            onclick={() => engine?.setBrush(id)}>{label}</button
          >
        {/each}
      </div>
      <div class="row">
        <span>Size {radius * 2 + 1}×{radius * 2 + 1}</span>
        <button onclick={() => engine?.setRadius(radius - 1)}>−</button>
        <button onclick={() => engine?.setRadius(radius + 1)}>+</button>
        <span>Strength {strength}</span>
        <button onclick={() => engine?.setStrength(strength - 0.25)}>−</button>
        <button onclick={() => engine?.setStrength(strength + 0.25)}>+</button>
      </div>
      <p class="muted small">
        Click or drag to paint; hold to keep raising, lowering or smoothing.
        Shift erases. Flatten levels everything to the height where you started;
        Set to level uses the Level above. Dots are terrain corners: a cell is
        ground once its four corners exist, so paint onto bare ground to make
        new terrain. Heights are in levels (a block is 1). Ground left at level
        0 over floor tiles flickers, so remove the tiles or lower it a touch.
      </p>
    {/if}
    {#if selected}
      <h3>Selected: {selected.piece}</h3>
      <div class="fields">
        <label
          >X <input
            type="number"
            step="0.25"
            value={selected.x}
            onchange={field((x) => ({ x }))}
          /></label
        >
        <label
          >Z <input
            type="number"
            step="0.25"
            value={selected.z}
            onchange={field((z) => ({ z }))}
          /></label
        >
        <label
          >Level <input
            type="number"
            step="0.1"
            value={selected.level}
            onchange={field((level) => ({ level }))}
          /></label
        >
        <label
          >Rotation° <input
            type="number"
            step="15"
            value={selected.rot * 90}
            onchange={field((deg) => ({ rot: (((deg / 90) % 4) + 4) % 4 }))}
          /></label
        >
        <label
          >Scale <input
            type="number"
            step="0.05"
            min="0.25"
            max="4"
            value={selected.scale ?? 1}
            onchange={field((s) => ({ scale: Math.max(0.25, Math.min(4, s)) }))}
          /></label
        >
      </div>
      <div class="row">
        <button onclick={() => engine?.deleteSelected()}>Delete</button>
        <button onclick={() => engine?.select(null)}>Deselect</button>
      </div>
    {/if}
    <p class="muted">
      {counts.pieces} pieces · {counts.spawns}/6 spawns · {counts.dummies}/{MAX_DUMMIES}
      dummies
    </p>
    {#if tool.kind === 'dummy'}
      <p class="muted small">
        A dummy stands exactly where you point on any floor, deck or ground you
        can see (Level only applies over empty space). Placing another once
        there are {MAX_DUMMIES} replaces the oldest.
      </p>
    {/if}
    <p class="muted small">
      Left click place · Shift+click or Erase removes whatever is under the
      pointer · Select picks any piece to edit or delete (Delete key, Esc to
      deselect) · Arrows or WASD pan · Right drag orbit · Shift+right drag or
      middle drag pan · Wheel zooms toward the pointer · R rotate (Shift+R 45°)
      · Alt snaps to half tiles · − = scale · [ ] level · Ctrl+Z undo. X and Z
      are in tiles, a level is half a tile (one block or stair high).
    </p>
    {#if status}<p class="status">{status}</p>{/if}
  </aside>
  <div class="view"><canvas bind:this={canvas}></canvas></div>
</div>

<style>
  .editor {
    display: grid;
    grid-template-columns: 320px 1fr;
    height: 100%;
  }
  aside {
    overflow-y: auto;
    border-radius: 0;
  }
  aside a {
    color: var(--gold);
  }
  .view {
    position: relative;
    min-width: 0;
  }
  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin: 6px 0;
  }
  .search {
    width: 100%;
    margin-bottom: 6px;
  }
  summary {
    cursor: pointer;
    margin: 4px 0;
  }
  .fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .fields input {
    width: 100%;
  }
  .palette {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
  }
  .palette button,
  .small {
    font-size: 12px;
    padding: 4px 6px;
  }
  .active {
    border-color: #fff;
    background: #5a4520;
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
  }
  .status {
    color: var(--gold);
  }
  label input {
    width: 100%;
  }
</style>
